import React from 'react';

interface BlogContentProps {
  content: string;
}

const BlogContent: React.FC<BlogContentProps> = ({ content }) => {
  // Función para convertir markdown básico a HTML
  const markdownToHtml = (markdown: string): string => {
    // Guardar code blocks con placeholders
    const codeBlocks: string[] = [];
    let html = markdown.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
      const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
      const escapedCode = code
        .trim()
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      codeBlocks.push(`<pre class="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-6 font-mono text-sm"><code class="language-${lang || 'typescript'}">${escapedCode}</code></pre>`);
      return placeholder;
    });

    // Headers (después de code blocks)
    html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-8 mb-4 text-gray-900">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold mt-10 mb-5 text-gray-900">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold mt-10 mb-5 text-gray-900">$1</h1>');

    // Inline code (después de code blocks para evitar conflictos)
    html = html.replace(/`([^`\n]+)`/g, '<code class="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>');

    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer">$1</a>');

    // Lists - procesar líneas que empiezan con -
    const lines = html.split('\n');
    let inList = false;
    const processedLines: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isListItem = /^- (.+)$/.test(line.trim());

      if (isListItem) {
        if (!inList) {
          processedLines.push('<ul class="list-disc list-inside my-4 space-y-2 ml-4">');
          inList = true;
        }
        const content = line.replace(/^- (.+)$/, '$1');
        // Procesar contenido de la lista (bold, links, etc.)
        let processedContent = content
          .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
          .replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');
        processedLines.push(`<li class="mb-2">${processedContent}</li>`);
      } else {
        if (inList) {
          processedLines.push('</ul>');
          inList = false;
        }
        processedLines.push(line);
      }
    }

    if (inList) {
      processedLines.push('</ul>');
    }

    html = processedLines.join('\n');

    // Paragraphs - solo para líneas que no son headers, listas o code blocks
    html = html.split('\n\n').map(para => {
      const trimmed = para.trim();
      if (!trimmed) return '';
      if (trimmed.startsWith('<') || trimmed.includes('__CODE_BLOCK_')) {
        return trimmed;
      }
      // Procesar contenido del párrafo
      let processedPara = trimmed
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
        .replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono">$1</code>')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary hover:underline font-medium" target="_blank" rel="noopener noreferrer">$1</a>');
      return `<p class="mb-4 leading-relaxed text-gray-700">${processedPara}</p>`;
    }).filter(p => p).join('\n');

    // Restaurar code blocks
    codeBlocks.forEach((block, index) => {
      html = html.replace(`__CODE_BLOCK_${index}__`, block);
    });

    return html;
  };

  const htmlContent = markdownToHtml(content);

  return (
    <div
      className="blog-content text-gray-700"
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default BlogContent;
